import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getToken } from "../utils/auth";
import HumanLoader from "../components/loaders/HumanLoader";
import "./ViewStudentCampusListPage.css";

export default function CampusListPage() {
  const [campuses, setCampuses] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  /* ---------- FETCH CAMPUSES (MIN 3s LOADER) ---------- */
  useEffect(() => {
    let isMounted = true;
    const fetchCampuses = async () => {
      const startTime = Date.now();
      setPageLoading(true);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/campus/get`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );

        if (!res.ok) throw new Error("Failed to fetch campuses");

        const data = await res.json();
        if (isMounted) setCampuses(data);
      } catch (err) {
        console.error(err);
        if (isMounted) setError("Unable to load campuses. Please try again later.");
      } finally {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(3000 - elapsed, 0);
        setTimeout(() => {
          if (isMounted) setPageLoading(false);
        }, remaining);
      }
    };

    fetchCampuses();
    return () => { isMounted = false; };
  }, []);

  const handleCampusClick = (college) => {
    localStorage.setItem("selectedCampus", college);
    navigate("/view-students-year", { state: { campus: college } });
  };

  /* ---------- LOADER ---------- */
  if (pageLoading) {
    return <HumanLoader text="Loading campuses" />;
  }

  return (
    <main className="campus-page-wrapper">
      <div className="campus-container">
        
        {/* Header Section */}
        <header className="campus-header">
          <div className="badge">Campus Directory</div>
          <h1 className="campus-title">Select Your Campus</h1>
          <p className="campus-subtitle">
            Choose an institution below to view academic records and student insights.
          </p>
        </header>

        {/* State Alerts */}
        {error && (
          <div className="status-banner error" role="alert">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {!error && campuses.length === 0 && (
          <div className="status-banner empty">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <span>No campuses found in the database.</span>
          </div>
        )}

        {/* Grid List */}
        {!error && campuses.length > 0 && (
          <section className="campus-grid">
            {campuses.map((campus) => (
              <article
                key={campus._id}
                className="campus-card"
                onClick={() => handleCampusClick(campus.college)}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => e.key === 'Enter' && handleCampusClick(campus.college)}
              >
                <div className="campus-card-content">
                  <div className="campus-icon">
                    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m0 0h4m-4 0V10m0 0h4m-4 0H7" />
                    </svg>
                  </div>
                  <h2 className="campus-name">{campus.college}</h2>
                </div>

                <div className="campus-action">
                  <span>Continue</span>
                  <svg className="arrow-icon" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </article>
            ))}
          </section>
        )}

      </div>
    </main>
  );
}